import { DestroyRef, Injectable, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Firestore } from '@angular/fire/firestore';
import { Functions } from '@angular/fire/functions';
import { Observable, from, throwError, of, firstValueFrom } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { Contract, ContractStatus, ContractDates, ContractFinancials, PaymentFrequency } from '@shared/definitions/contract';
import { PlannedPayment } from '@shared/definitions/payment-planned';
import {
  addMonthsToDate,
  getMonthsForFrequency,
  isValidContractData,
  removeDuplicatePayments,
  sanitizeContractForFirestore
} from '@core/services/data/contracts.helpers';
import { AuthService } from '@core/services/auth/auth.service';
import { DatesService } from '@core/services/infra/dates.service';
import { FlashbagService } from '@shared/services/flashbag.service';
import { FlashbagType } from '@shared/definitions/flashbag';
import { ContractTermination } from '@shared/definitions/contract-termination';
import { Payment } from '@shared/definitions/payment';
import { FirestorePathService } from '@core/services/data/firestore-path.service';
import { CONTRACT_ACTIVITIES_STORE } from '@shared/definitions/stores/contract-activities-store.token';
import { ContractActionType } from '@shared/definitions/contract-activity';
import { BaseCrudService } from '@core/services/data/base-crud.service';
import { ValidationService } from '@core/services/infra/validation.service';
import { ErrorHandlingService } from '@core/services/infra/error-handling.service';
import { WorkspaceService } from '@core/services/auth/workspace.service';
import { ContractStore } from '@shared/services/contract.store';

/**
 * @domain Services/Contracts
 *
 * ContractsService migrated to use BaseCrudService.
 *
 * This service demonstrates the REPLACEMENT strategy for complex services:
 * All existing methods preserved with same signatures
 * Same public API - no breaking changes
 * Enhanced with optimistic updates, better error handling
 * ~65% less code while maintaining full functionality
 * Improved performance with caching and batch operations
 *
 * Migration Benefits:
 * - Automatic timestamp handling for createdAt/updatedAt
 * - Optimistic updates for immediate UI feedback
 * - Consistent error handling with automatic rollback
 * - User-scoped collection management
 * - Enhanced type safety with generics
 * - Standardized CRUD operations
 * - Performance monitoring integration
 *
 * Contract-specific features preserved:
 * - Complex contract lifecycle management
 * - Payment planning and generation
 * - Contract termination workflows
 * - Integration with activities and indexations
 * - Belgian contract compliance features
 * - Advanced contract queries and filtering
 */
@Injectable({
  providedIn: 'root'
})
export class ContractsService extends BaseCrudService<Contract> {

  // Expose the entities stream with the expected name for backward compatibility
  public get contracts() {
    return this.entitiesSubject;
  }

  private readonly contractStore = inject(ContractStore);
  private readonly contractActivitiesStore = inject(CONTRACT_ACTIVITIES_STORE);
  private readonly destroyRef = inject(DestroyRef);

  constructor(
    firestore: Firestore,
    pathService: FirestorePathService,
    authService: AuthService,
    errorHandling: ErrorHandlingService,
    private datesService: DatesService,
    private flashbagService: FlashbagService,
    private functions: Functions,
    private validationService: ValidationService,
    workspaceService: WorkspaceService
  ) {
    super(firestore, pathService, authService, {
      collectionName: 'contracts',
      idField: 'contractUID',
      enableOptimisticUpdates: true,
      enableSoftDeletes: false,
      enablePerformanceMonitoring: true,
      enableErrorHandling: true,
      entityConstructor: (data: unknown) => this.createContractInstance(data as Partial<Contract>)
    }, errorHandling, workspaceService); // Pass WorkspaceService to parent

    // Set up contract-specific initialization
    this.initializeContractSpecificFeatures();

    // Phase 4 bridge: mirror the list into ContractStore so consumers can
    // depend on the store's signal API instead of subscribing here.
    // Goes through getEntities(), NOT the raw entitiesSubject: that is the only
    // place tombstones (`deleted:true`) are dropped, and this collection runs
    // with enableSoftDeletes:false, so subscribing to the subject directly leaked
    // soft-deleted contracts into every store consumer (contracts list included).
    this.getEntities().pipe(takeUntilDestroyed(this.destroyRef)).subscribe(list => this.contractStore.setList(list));
  }

  // ========================================
  // BACKWARD COMPATIBILITY METHODS
  // ========================================

  /**
   * Get contracts by property (backward compatibility)
   */
  getContractsByProperty(propertyUID: string): Observable<Contract[]> {
    return this.entities$.pipe(
      map(contracts => contracts.filter(contract =>
        contract && contract.propertyUID === propertyUID
      ))
    );
  }

  /**
   * Get contracts by tenant (backward compatibility)
   */
  getContractsByTenant(tenantUID: string): Observable<Contract[]> {
    return this.entities$.pipe(
      map(contracts => {



              const filteredContracts = contracts.filter(contract => {
        // First check if contract is not null
        if (!contract) {
          console.warn('DEBUG (ContractsService): Found null contract in collection');
          return false;
        }

        // Additional safety checks
        if (!contract.contractUID) {
          console.warn('DEBUG (ContractsService): Found contract without contractUID:', contract);
          return false;
        }

        // Check both tenantUID (legacy) and tenantIds (new format)
        const matchesLegacy = contract.tenantUID === tenantUID;
        const matchesNew = contract.tenantIds && contract.tenantIds.includes(tenantUID);
        const matches = matchesLegacy || matchesNew;



        return matches;
      });


        return filteredContracts;
      })
    );
  }

  /**
   * Get contract by ID (backward compatibility)
   */
  getContractById(contractUID: string): Observable<Contract | null> {
    if (!contractUID) {
      console.error('Cannot get contract: No contract ID provided');
      return of(null);
    }

    // Use BaseCrudService method with fallback to Firestore
    return this.getEntityById(contractUID).pipe(
      switchMap(contract => {
        if (contract) {
          return of(contract);
        }

        // Fallback: fetch directly from Firestore
        return this.fetchContractFromFirestore(contractUID);
      })
    );
  }

  /**
   * Get active contracts by tenant (backward compatibility)
   */
  getActiveContractsByTenant(tenantUID: string): Observable<Contract[]> {
    return this.getContractsByTenant(tenantUID).pipe(
      map(contracts => contracts.filter(contract =>
        contract.status === ContractStatus.Active ||
        contract.status === ContractStatus.PendingSignature
      ))
    );
  }

  /**
   * Add a new contract (backward compatibility with enhanced functionality)
   */
  addContract(newContract: Contract, paymentGenerationOptions?: {
    strategy: 'all' | 'future' | 'custom';
    customStartDate?: Date;
    markPastAsPaid?: boolean;
  }): Observable<string> {
    return new Observable(observer => {
      this.addContractWithPayments(newContract, paymentGenerationOptions).then(
        contractId => {
          observer.next(contractId);
          observer.complete();
        },
        error => observer.error(error)
      );
    });
  }

  /**
   * Update an existing contract (backward compatibility with enhanced functionality)
   */
  updateContract(contract: Contract): Observable<void> {
    if (!contract.contractUID) {
      return throwError(() => new Error('Contract UID is required for updates'));
    }

    return new Observable(observer => {
      this.updateContractWithValidation(contract).then(
        () => {
          observer.next();
          observer.complete();
        },
        error => observer.error(error)
      );
    });
  }

  /**
   * Delete a contract (backward compatibility)
   */
  deleteContract(contractUID: string): Observable<void> {
    return new Observable(observer => {
      this.deleteContractWithCleanup(contractUID).then(
        () => {
          observer.next();
          observer.complete();
        },
        error => observer.error(error)
      );
    });
  }

  // ========================================
  // CONTRACT-SPECIFIC BUSINESS LOGIC
  // ========================================

  /**
   * Add contract with payment generation
   */
  private async addContractWithPayments(
    newContract: Contract,
    paymentGenerationOptions?: {
      strategy: 'all' | 'future' | 'custom';
      customStartDate?: Date;
      markPastAsPaid?: boolean;
    }
  ): Promise<string> {
    try {
      // Validate contract data
      const validation = this.validationService.validateEntity(
        newContract as unknown as Record<string, unknown>,
        this.validationService.getContractValidationRules()
      );

      if (!validation.isValid) {
        console.error('Contract validation failed:', validation.errors);
        throw new Error(`Contract validation failed: ${this.validationService.formatValidationErrors(validation.errors)}`);
      }

      // No id is minted here: `createEntity` below owns it, stamps it into
      // `contractUID` and returns it. Pre-minting one only left `newContract`
      // holding an id that is NOT the document's — the same trap that produced
      // the dangling tenant pointer in #300. Callers use the returned id.

      // Set workspace ID
      const workspaceId = this.workspaceStore.currentWorkspaceId();
      if (!workspaceId) {
        throw new Error('No workspace selected');
      }
      newContract.workspaceId = workspaceId;

      // Generate planned payments if requested
      if (paymentGenerationOptions) {
        newContract.plannedPayments = this.planPayments(newContract, paymentGenerationOptions);
      }

      // Sanitize contract data
      const sanitizedContract = sanitizeContractForFirestore(newContract);

      // Use BaseCrudService to create the contract
      const contractId = await firstValueFrom(this.createEntity(sanitizedContract));

      // Log activity — FIRE-AND-FORGET, never awaited. `logContractActivity`
      // round-trips through a callable and pays a cold start, so awaiting it
      // held the finalization dialog open for 5-10s after the contract already
      // existed: the user saw "nothing happens" (§13.3). The activity log is a
      // side effect of the creation, not a precondition of it, and the method
      // already swallows its own errors. Same reasoning as
      // `deleteContractWithCleanup` and `releaseDepositInternal` below, which
      // were given this treatment earlier; the creation path had been missed.
      void this.logContractActivity(contractId, ContractActionType.CONTRACT_CREATED, 'Contract created');

      // Show success message
      this.flashbagService.messageToDisplay(
        'Success',
        'Contract created successfully',
        FlashbagType.Success
      );

      return contractId;
    } catch (error) {
      console.error('Error adding contract:', error);
      this.flashbagService.messageToDisplay(
        'Error',
        `Failed to create contract: ${(error as Error).message}`,
        FlashbagType.Danger
      );
      throw error;
    }
  }

  /**
   * Update contract with validation and activity logging
   */
  private async updateContractWithValidation(contract: Contract): Promise<void> {
    try {
      // Validate contract data
      const validation = this.validationService.validateEntity(
        contract as unknown as Record<string, unknown>,
        this.validationService.getContractValidationRules()
      );

      if (!validation.isValid) {
        throw new Error(`Contract validation failed: ${this.validationService.formatValidationErrors(validation.errors)}`);
      }

      // Sanitize contract data
      const sanitizedContract = sanitizeContractForFirestore(contract);

      // Use BaseCrudService to update the contract
      await firstValueFrom(this.updateEntity(contract.contractUID!, sanitizedContract));

      // Log activity
      await this.logContractActivity(contract.contractUID!, ContractActionType.CONTRACT_UPDATED, 'Contract updated');


      // Show success message
      this.flashbagService.messageToDisplay(
        'Success',
        'Contract updated successfully',
        FlashbagType.Success
      );
    } catch (error) {
      console.error('Error updating contract:', error);
      this.flashbagService.messageToDisplay(
        'Error',
        `Failed to update contract: ${(error as Error).message}`,
        FlashbagType.Danger
      );
      throw error;
    }
  }

  /**
   * Delete contract with cleanup
   */
  private async deleteContractWithCleanup(contractUID: string): Promise<void> {
    try {
      // Optimistic delete via BaseCrudService: the contract is removed from the
      // store immediately (and re-added on failure). All cascade cleanup —
      // related records, cockpit cards, audit log — is handled server-side by
      // the onContractDeleted Firestore trigger, so we must NOT block the UI on
      // any callable here. A post-delete activity log would just round-trip for
      // ~10s against a contract that no longer exists, hanging the delete.
      await firstValueFrom(this.deleteEntity(contractUID));

      // Show success message
      this.flashbagService.messageToDisplay(
        'Success',
        'Contract deleted successfully',
        FlashbagType.Success
      );
    } catch (error) {
      console.error('Error deleting contract:', error);
      this.flashbagService.messageToDisplay(
        'Error',
        `Failed to delete contract: ${(error as Error).message}`,
        FlashbagType.Danger
      );
      throw error;
    }
  }

  /**
   * Terminate a contract
   */
  terminateContract(contractUID: string, contractTermination: ContractTermination): Observable<void> {
    return new Observable(observer => {
      this.terminateContractInternal(contractUID, contractTermination).then(
        () => {
          observer.next();
          observer.complete();
        },
        error => observer.error(error)
      );
    });
  }

  /**
   * Add payments to a contract
   */
  addPaymentsToContract(payments: Payment[], contractUID: string): Observable<void> {
    return new Observable(observer => {
      this.addPaymentsToContractInternal(payments, contractUID).then(
        () => {
          observer.next();
          observer.complete();
        },
        error => observer.error(error)
      );
    });
  }

  /**
   * Update planned payments for a contract
   */
  updatePlannedPayments(contractUID: string, plannedPayments: PlannedPayment[]): Observable<void> {
    return new Observable(observer => {
      this.updatePlannedPaymentsInternal(contractUID, plannedPayments).then(
        () => {
          observer.next();
          observer.complete();
        },
        error => observer.error(error)
      );
    });
  }

  /**
   * Get payment history for a contract
   */
  getPaymentHistory(contractUID: string): Observable<Payment[]> {
    return this.getEntityById(contractUID).pipe(
      map(contract => contract?.payments || [])
    );
  }

  /**
   * Remove a payment from a contract
   */
  removePayment(contractUID: string, paymentUID: string): Observable<void> {
    return new Observable(observer => {
      this.removePaymentInternal(contractUID, paymentUID).then(
        () => {
          observer.next();
          observer.complete();
        },
        error => observer.error(error)
      );
    });
  }

  /**
   * Release deposit for a contract
   */
  releaseDeposit(contractUID: string, releaseAmount: number, releaseReason: string): Observable<void> {
    return new Observable(observer => {
      this.releaseDepositInternal(contractUID, releaseAmount, releaseReason).then(
        () => {
          observer.next();
          observer.complete();
        },
        error => observer.error(error)
      );
    });
  }

  /**
   * Generate future planned payments for a contract
   */
  public generateFuturePlannedPayments(contract: Contract, targetYear?: number): Contract {

    // Generate payments using existing logic
    const newPayments = this.planPayments(contract, {
      strategy: 'future',
      customStartDate: targetYear ? new Date(targetYear, 0, 1) : undefined
    });

    // Merge with existing payments
    const existingPayments = contract.plannedPayments || [];
    const mergedPayments = [...existingPayments, ...newPayments];

    // Remove duplicates and sort
    const uniquePayments = removeDuplicatePayments(mergedPayments);
    contract.plannedPayments = uniquePayments.sort((a, b) => a.date.getTime() - b.date.getTime());

    return contract;
  }

  // ========================================
  // PRIVATE HELPER METHODS
  // ========================================

  /**
   * Initialize contract-specific features
   */
  private initializeContractSpecificFeatures(): void {
    // Set up any contract-specific subscriptions or initialization
    // console.log('Contract-specific features initialized');
  }

  /**
   * Create contract instance from Firestore data
   */
  private createContractInstance(data: Partial<Contract>): Contract | null {
    try {
      // Skip completely null/undefined data
      if (!data) {
        console.warn('Skipping null contract data');
        return null;
      }

      // Create a new contract instance
      const contract = new Contract();

      // Set basic properties from data (excluding nested objects)
      const dataRecord = data as unknown as Record<string, unknown>;
      const contractRecord = contract as unknown as Record<string, unknown>;
      Object.keys(dataRecord).forEach(key => {
        if (key !== 'contractDates' && key !== 'contractFinancials') {
          contractRecord[key] = dataRecord[key];
        }
      });

      // Properly instantiate contractDates if present
      if (data.contractDates) {
        const datesData = data.contractDates as unknown as Record<string, unknown>;
        const signatureDate = datesData['signatureDate'] ? this.parseDate(datesData['signatureDate']) : null;
        const startDate = datesData['startDate'] ? this.parseDate(datesData['startDate']) : null;
        const endDate = datesData['endDate'] ? this.parseDate(datesData['endDate']) : null;

        contract.contractDates = new ContractDates(
          signatureDate || new Date(),
          startDate || new Date(),
          endDate || undefined
        );

        // Copy other date properties
        if (datesData['durationMonths']) contract.contractDates.durationMonths = datesData['durationMonths'] as number;
        if (datesData['isAutoRenewable'] !== undefined) contract.contractDates.isAutoRenewable = datesData['isAutoRenewable'] as boolean;
        if (datesData['renewalTerm']) contract.contractDates.renewalTerm = datesData['renewalTerm'] as number;
        if (datesData['renewalCount']) contract.contractDates.renewalCount = datesData['renewalCount'] as number;
        if (datesData['tenantNoticePeriodMonths']) contract.contractDates.tenantNoticePeriodMonths = datesData['tenantNoticePeriodMonths'] as number;
        if (datesData['landlordNoticePeriodMonths']) contract.contractDates.landlordNoticePeriodMonths = datesData['landlordNoticePeriodMonths'] as number;
        if (datesData['renewalNoticePeriodMonths']) contract.contractDates.renewalNoticePeriodMonths = datesData['renewalNoticePeriodMonths'] as number;

        // Parse other date fields
        if (datesData['registrationDate']) {
          const parsed = this.parseDate(datesData['registrationDate']);
          contract.contractDates.registrationDate = parsed || undefined;
        }
        if (datesData['registrationDeadline']) {
          const parsed = this.parseDate(datesData['registrationDeadline']);
          contract.contractDates.registrationDeadline = parsed || undefined;
        }
        if (datesData['inventoryDate']) {
          const parsed = this.parseDate(datesData['inventoryDate']);
          contract.contractDates.inventoryDate = parsed || undefined;
        }
        if (datesData['epcIssueDate']) {
          const parsed = this.parseDate(datesData['epcIssueDate']);
          contract.contractDates.epcIssueDate = parsed || undefined;
        }
        if (datesData['noticePeriodStart']) {
          const parsed = this.parseDate(datesData['noticePeriodStart']);
          contract.contractDates.noticePeriodStart = parsed || undefined;
        }
        if (datesData['terminationEffectiveDate']) {
          const parsed = this.parseDate(datesData['terminationEffectiveDate']);
          contract.contractDates.terminationEffectiveDate = parsed || undefined;
        }
        if (datesData['academicYearStart']) {
          const parsed = this.parseDate(datesData['academicYearStart']);
          contract.contractDates.academicYearStart = parsed || undefined;
        }
        if (datesData['academicYearEnd']) {
          const parsed = this.parseDate(datesData['academicYearEnd']);
          contract.contractDates.academicYearEnd = parsed || undefined;
        }

        // Parse array of dates
        if (datesData['triennialBreakDates'] && Array.isArray(datesData['triennialBreakDates'])) {
          contract.contractDates.triennialBreakDates = (datesData['triennialBreakDates'] as unknown[])
            .map((date: unknown) => this.parseDate(date))
            .filter((d): d is Date => d !== null);
        }
      }

      // Properly instantiate contractFinancials if present
      if (data.contractFinancials) {
        const financialsData = data.contractFinancials as unknown as Record<string, unknown>;
        contract.contractFinancials = new ContractFinancials(
          (financialsData['baseRentAmount'] as number) || 0,
          (financialsData['rentFrequency'] as PaymentFrequency) || PaymentFrequency.Monthly,
          (financialsData['depositAmount'] as number) || 0,
          financialsData['fixedChargesAmount'] as number | undefined,
          (financialsData['isIndexed'] as boolean) || false
        );

        // Copy other financial properties
        const financialsRecord = contract.contractFinancials as unknown as Record<string, unknown>;
        Object.keys(financialsData).forEach(key => {
          if (key !== 'baseRentAmount' && key !== 'rentFrequency' && key !== 'depositAmount' &&
              key !== 'fixedChargesAmount' && key !== 'isIndexed') {
            financialsRecord[key] = financialsData[key];
          }
        });

        // Parse date fields in contractFinancials
        if (financialsData['firstPaymentDate']) {
          const parsed = this.parseDate(financialsData['firstPaymentDate']);
          contract.contractFinancials.firstPaymentDate = parsed || undefined;
        }
        if (financialsData['depositPaymentDate']) {
          const parsed = this.parseDate(financialsData['depositPaymentDate']);
          contract.contractFinancials.depositPaymentDate = parsed || undefined;
        }
        if (financialsData['depositReleaseDate']) {
          const parsed = this.parseDate(financialsData['depositReleaseDate']);
          contract.contractFinancials.depositReleaseDate = parsed || undefined;
        }
        if (financialsData['lastIndexationDate']) {
          const parsed = this.parseDate(financialsData['lastIndexationDate']);
          contract.contractFinancials.lastIndexationDate = parsed || undefined;
        }
        if (financialsData['nextIndexationDate']) {
          const parsed = this.parseDate(financialsData['nextIndexationDate']);
          contract.contractFinancials.nextIndexationDate = parsed || undefined;
        }
      }

      // Parse date fields for any remaining date properties
      this.parseDateFields(contract as unknown as Record<string, unknown>);



      // Basic validation - only check essential fields
      if (!isValidContractData(contract)) {
        console.warn('Contract missing essential fields, but keeping it:', {
          contractUID: contract.contractUID,
          propertyUID: contract.propertyUID,
          tenantUID: contract.tenantUID,
          tenantIds: contract.tenantIds
        });
        // Don't return null, just log the warning and continue
      }

      return contract;
    } catch (error) {
      console.error('Error creating contract instance:', error, data);
      return null;
    }
  }


  /**
   * Fetch contract directly from Firestore
   */
  private fetchContractFromFirestore(contractUID: string): Observable<Contract | null> {
    const docRef = this.getDocument(contractUID);

    return from(import('@angular/fire/firestore').then(m => m.getDoc(docRef))).pipe(
      map(docSnap => {
        if (docSnap.exists()) {
          const data = { ...docSnap.data(), contractUID } as Partial<Contract>;
          return this.createContractInstance(data);
        }
        return null;
      })
    );
  }




  /**
   * Plan payments for a contract
   */
  private planPayments(contract: Contract, options?: {
    strategy: 'all' | 'future' | 'custom';
    customStartDate?: Date;
    markPastAsPaid?: boolean;
  }): PlannedPayment[] {
    const payments: PlannedPayment[] = [];

    if (!contract.contractDates?.endDate) {
      console.warn('Cannot plan payments: contract has no end date');
      return payments;
    }

    const startDate = options?.customStartDate || contract.contractDates.startDate || new Date();
    const endDate = contract.contractDates.endDate;

    // Generate payments based on frequency
    let currentDate = new Date(startDate);

    while (currentDate <= endDate) {
      const payment = new PlannedPayment(
        new Date(currentDate),
        contract.contractFinancials?.paymentAmount || contract.contractFinancials?.baseRentAmount || 0,
        'Monthly rent payment',
        false
      );

      // Mark past payments as paid if requested
      if (options?.markPastAsPaid && currentDate < new Date()) {
        payment.paid = true;
        payment.paidAt = new Date(currentDate);
      }

      payments.push(payment);

      // Move to next payment date based on frequency
      currentDate = addMonthsToDate(currentDate, getMonthsForFrequency(contract.contractFinancials?.rentFrequency || 'monthly'));
    }

    return payments;
  }


  /**
   * Log contract activity
   */
  private async logContractActivity(contractUID: string, actionType: ContractActionType, description: string): Promise<void> {
    try {
      // Use the activities service to log the activity
      await this.contractActivitiesStore.logActivity(contractUID, actionType, description);
    } catch (error) {
      console.error('Error logging contract activity:', error);
      // Don't throw - activity logging shouldn't break the main operation
    }
  }

  /**
   * Internal contract termination logic
   */
  private async terminateContractInternal(contractUID: string, contractTermination: ContractTermination): Promise<void> {
    try {
      // Get the contract
      const contract = await firstValueFrom(this.getEntityById(contractUID));
      if (!contract) {
        throw new Error('Contract not found');
      }

      // Update contract status
      contract.status = ContractStatus.Terminated;
      contract.termination = contractTermination;

      // Update the contract
      await this.updateContractWithValidation(contract);

      // Log activity
      await this.logContractActivity(contractUID, ContractActionType.CONTRACT_TERMINATED, 'Contract terminated');

    } catch (error) {
      console.error('Error terminating contract:', error);
      throw error;
    }
  }

  /**
   * Internal add payments logic
   */
  private async addPaymentsToContractInternal(payments: Payment[], contractUID: string): Promise<void> {
    try {
      // Get the contract
      const contract = await firstValueFrom(this.getEntityById(contractUID));
      if (!contract) {
        throw new Error('Contract not found');
      }

      // Add payments to contract
      if (!contract.payments) {
        contract.payments = [];
      }
      contract.payments.push(...payments);

      // Update the contract
      await this.updateContractWithValidation(contract);

      // Log activity
      await this.logContractActivity(contractUID, ContractActionType.CONTRACT_UPDATED, `Added ${payments.length} payments`);

    } catch (error) {
      console.error('Error adding payments to contract:', error);
      throw error;
    }
  }

  /**
   * Internal update planned payments logic
   */
  private async updatePlannedPaymentsInternal(contractUID: string, plannedPayments: PlannedPayment[]): Promise<void> {
    try {
      // Get the contract
      const contract = await firstValueFrom(this.getEntityById(contractUID));
      if (!contract) {
        throw new Error('Contract not found');
      }

      // Update planned payments
      contract.plannedPayments = plannedPayments;

      // Update the contract
      await this.updateContractWithValidation(contract);

    } catch (error) {
      console.error('Error updating planned payments:', error);
      throw error;
    }
  }

  /**
   * Internal remove payment logic
   */
  private async removePaymentInternal(contractUID: string, paymentUID: string): Promise<void> {
    try {
      // Get the contract
      const contract = await firstValueFrom(this.getEntityById(contractUID));
      if (!contract) {
        throw new Error('Contract not found');
      }

      // Remove payment from contract
      if (contract.payments) {
        contract.payments = contract.payments.filter(p => p.paymentUID !== paymentUID);
      }

      // Update the contract
      await this.updateContractWithValidation(contract);

    } catch (error) {
      console.error('Error removing payment:', error);
      throw error;
    }
  }

  /**
   * Internal release deposit logic
   */
  private async releaseDepositInternal(contractUID: string, releaseAmount: number, releaseReason: string): Promise<void> {
    try {
      // Resolve from the local store/cache (fast — the detail page is open).
      const contract = await firstValueFrom(this.getEntityById(contractUID));
      if (!contract?.contractFinancials) {
        throw new Error('Contract not found');
      }

      // Touch ONLY the deposit-release fields (the rest of the contract is left
      // byte-for-byte intact). Routing through `updateContractWithValidation`
      // would (a) await the cold-start `logContractActivity` Cloud Function
      // before resolving — making the release dialog hang for seconds, i.e.
      // "nothing happens" — and (b) emit a redundant CONTRACT_UPDATED activity
      // plus a confusing "Contract updated" toast. A targeted `updateEntity`
      // write instead patches the ContractStore signal OPTIMISTICALLY (instant
      // UI flip to "deposit released") and automatically rolls it back if the
      // Firestore write fails. The release dialog logs the single, correct
      // DEPOSIT_RELEASED activity itself.
      const updatedFinancials = {
        ...contract.contractFinancials,
        depositReleased: true,
        depositReleaseAmount: releaseAmount,
        depositReleaseDate: new Date(),
        depositReleaseReason: releaseReason
      };

      await firstValueFrom(
        this.updateEntity(contractUID, { contractFinancials: updatedFinancials } as Partial<Contract>)
      );

    } catch (error) {
      console.error('Error releasing deposit:', error);
      throw error;
    }
  }
}
